import React, { useRef, useState, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { Box, Typography, useMediaQuery, useTheme, Tooltip, ToggleButtonGroup, ToggleButton, IconButton, Popover } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import es from 'date-fns/locale/es';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import { getAppointments, updateAppointment } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useCalendarData } from '../hooks/useCalendarData';
import { useNotification } from '../hooks/useNotification';
import AppointmentDialog from './AppointmentDialog';
import WeekOverviewGrid from './WeekOverviewGrid';
import { APPOINTMENT_STATE_CONFIG as STATE_CONFIG } from '../constants/appointmentStates';

// El fondo SIEMPRE refleja el estado real del turno — es la señal operativa
// principal (CLAUDE.md). Antes is_overbooked pisaba el color acá, así que
// un turno atendido Y un turno pendiente, ambos sobreturno, se veían
// IDÉNTICOS (los dos naranja), perdiendo la señal de estado. Bug real
// reportado por el usuario 2026-10-27. Sobreturno ahora se indica con un
// patrón de rayas + badge "+1" en eventContent, nunca con el color de fondo.
const getEventColor = (ap) => STATE_CONFIG[ap.state]?.color ?? '#78909c';

const toLocalISO = (date) => {
  const pad = (n) => (n < 10 ? '0' + n : n);
  return (
    date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + 'T' +
    pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds())
  );
};

// Franja delgada al pie — el recepcionista/médico la usa las primeras
// semanas hasta memorizar los colores, después no compite por espacio con
// la grilla de trabajo activa. Una sola leyenda compartida entre la vista
// detallada y la de resumen semanal (antes cada una tenía la suya propia).
const Legend = ({ showOutOfSchedule }) => (
  <Box sx={{
    display: 'flex', flexWrap: 'wrap', gap: '5px 12px',
    px: 2, py: '5px', borderTop: '1px solid', borderColor: 'divider',
    bgcolor: 'grey.50', alignItems: 'center', flexShrink: 0,
  }}>
    {Object.entries(STATE_CONFIG).map(([key, cfg]) => (
      <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: cfg.color, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1 }}>
          {cfg.label}
        </Typography>
      </Box>
    ))}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Box sx={{
        width: 8, height: 8, borderRadius: '2px', bgcolor: '#546e7a', flexShrink: 0,
        backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.35) 0px, rgba(255,255,255,0.35) 2px, transparent 2px, transparent 4px)',
      }} />
      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1 }}>
        Sobreturno (rayas)
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Box sx={{
        width: 12, height: 12, borderRadius: '2px', bgcolor: '#546e7a', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: '0.55rem', color: 'white', fontWeight: 900, lineHeight: 1 }}>✓</Typography>
      </Box>
      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1 }}>
        Recepcionado
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#78909c', flexShrink: 0, borderLeft: '3px solid', borderColor: 'primary.main' }} />
      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1 }}>
        Franja = tipo de turno
      </Typography>
    </Box>
    {showOutOfSchedule && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#f0f0f0', border: '1px solid #ddd', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1 }}>
          Fuera de horario
        </Typography>
      </Box>
    )}
  </Box>
);

// Fecha/semana visible del calendario, persistida por usuario — antes
// CalendarGrid siempre arrancaba en "hoy" al volver a montarse (entrar de
// nuevo a /agenda, volver de la ficha de un paciente), obligando al
// usuario a navegar de nuevo hasta el día que estaba mirando. Pedido
// explícito del usuario (reportado por un médico y una recepcionista,
// 2026-11-02). Mismo patrón que lastDoctorKey en Dashboard.js.
const lastDateKey = (userId) => `vitanotas_last_calendar_date_${userId}`;

const CalendarGrid = ({ doctorId }) => {
  const calendarRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAdmin, isRecepcionista, isMedico, user } = useAuth();
  const notify = useNotification();
  // Médico tiene acceso total a la agenda de CUALQUIER médico de la
  // clínica (crear, arrastrar/redimensionar para reprogramar, editar
  // cualquier campo del turno) — el backend ya lo permite sin importar de
  // qué doctor_id se trate (ver routers/appointments.py, ampliado
  // 2026-10-09). Antes este componente seguía limitando al frontend a
  // "solo mi propia agenda" (isOwnAgenda), inconsistente con lo que el
  // backend ya permitía desde esa sesión — corregido 2026-10-13 junto con
  // habilitar crear turno desde el perfil del paciente, mismo criterio.
  const canEdit = isAdmin || isRecepcionista || isMedico;
  const canCreate = canEdit;

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showWeekOverview, setShowWeekOverview] = useState(false);

  // Navegación y título propios (no el headerToolbar nativo de FullCalendar,
  // que se veía como un sistema de botones distinto al resto de la app) —
  // se controlan vía la API imperativa de FullCalendar (calendarRef).
  const [calendarView, setCalendarView] = useState(isMobile ? 'timeGridDay' : 'timeGridWeek');
  const [calendarTitle, setCalendarTitle] = useState('');

  const updateCalendarTitle = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (api) setCalendarTitle(api.view.title);
    // Persistir la fecha activa en cada cambio de rango (prev/next/today/
    // gotoDate/cambio de vista) — ver lastDateKey arriba. api.getDate()
    // devuelve la fecha "de referencia" de la vista actual, que es
    // exactamente lo que gotoDate()/initialDate esperan de vuelta.
    if (api && user?.id) {
      try {
        localStorage.setItem(lastDateKey(user.id), api.getDate().toISOString());
      } catch {
        // localStorage puede fallar (modo privado, cuota llena) — no es
        // crítico, el calendario simplemente no recuerda la fecha.
      }
    }
  }, [user?.id]);

  const initialCalendarDate = useMemo(() => {
    if (!user?.id) return undefined;
    try {
      const saved = localStorage.getItem(lastDateKey(user.id));
      return saved || undefined;
    } catch {
      return undefined;
    }
  }, [user?.id]);

  const goToday = useCallback(() => { calendarRef.current?.getApi().today(); updateCalendarTitle(); }, [updateCalendarTitle]);
  const goPrev = useCallback(() => { calendarRef.current?.getApi().prev(); updateCalendarTitle(); }, [updateCalendarTitle]);
  const goNext = useCallback(() => { calendarRef.current?.getApi().next(); updateCalendarTitle(); }, [updateCalendarTitle]);
  const changeView = useCallback((view) => {
    calendarRef.current?.getApi().changeView(view);
    setCalendarView(view);
    updateCalendarTitle();
  }, [updateCalendarTitle]);

  // Ir a un día específico — pedido explícito del usuario: antes solo se
  // podía navegar de a una semana/día con las flechas o volver a "hoy",
  // sin forma de saltar directo a una fecha lejana (ej. revisar un turno
  // de hace 2 meses). gotoDate() es la API imperativa que FullCalendar ya
  // expone para esto — mismo patrón que goPrev/goNext/goToday de arriba.
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const goToDate = useCallback((date) => {
    if (!date) return;
    calendarRef.current?.getApi().gotoDate(date);
    updateCalendarTitle();
    setDatePickerAnchor(null);
  }, [updateCalendarTitle]);

  const { doctor, businessHours, patients, setPatients, waTemplates } = useCalendarData(doctorId);

  // Hora de inicio real del médico (ej. médico que empieza a las 8:00).
  // businessHours ya trae el horario semanal completo (ver
  // useCalendarData.js); false = sin horario configurado, se mantiene el
  // valor fijo de siempre. Pedido explícito del usuario con captura
  // 2026-10-29.
  const earliestStartHour = useMemo(() => {
    if (!businessHours || businessHours.length === 0) return null;
    const earliest = businessHours.reduce((min, slot) => (
      slot.startTime < min ? slot.startTime : min
    ), businessHours[0].startTime);
    return parseInt(earliest.split(':')[0], 10);
  }, [businessHours]);

  // scrollTime: directo a la hora real de inicio (sin margen) — antes
  // era 07:30 fijo para cualquier médico, obligando a scrollear manualmente.
  const earliestStartTime = earliestStartHour === null ? '07:30:00' : `${String(earliestStartHour).padStart(2, '0')}:00:00`;

  // slotMinTime: 1 HORA de margen antes del inicio real (no recortado al
  // segundo) — si recortara exacto, un turno excepcional cargado antes del
  // horario configurado (sobreturno temprano, cambio de horario posterior)
  // quedaría completamente OCULTO de la vista semana en vez de solo
  // grisado. Con el margen, ese caso raro sigue siendo visible sin
  // scrollear. Redondeado a hora en punto (nunca ":30") para no
  // desalinear las etiquetas "cada 1 hora" contra las horas reales — ver
  // slotLabelInterval. Pedido explícito del usuario 2026-10-29.
  const slotMinTime = earliestStartHour === null ? '07:00:00' : `${String(Math.max(0, earliestStartHour - 1)).padStart(2, '0')}:00:00`;

  // Días de la semana sin NINGÚN horario configurado para este médico —
  // se ocultan de la vista semana en vez de mostrarse vacíos con rayas de
  // "fuera de horario", agrandando el resto de los días visibles. Un
  // médico con horario en algún tramo de un día (aunque sea corto) sigue
  // mostrando ese día entero. Sin horario configurado en absoluto
  // (businessHours === false), no se oculta ningún día — no hay forma de
  // saber cuáles "no trabaja". Pedido explícito del usuario 2026-10-29.
  const autoHiddenWeekdays = useMemo(() => {
    if (!businessHours || businessHours.length === 0) return [];
    const workedDays = new Set(businessHours.flatMap((slot) => slot.daysOfWeek));
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    return allDays.filter((d) => !workedDays.has(d));
  }, [businessHours]);

  // Ocultar días sin horario es el default, pero no todos quieren eso (ej.
  // alguien que igual quiere ver el fin de semana vacío) — toggle explícito
  // para volver a mostrar todos los días. Pedido explícito del usuario
  // 2026-10-29, en la misma conversación que pidió el ocultamiento.
  const [showAllDays, setShowAllDays] = useState(false);
  const hiddenWeekdays = showAllDays ? [] : autoHiddenWeekdays;

  const refreshCalendar = useCallback(() => {
    calendarRef.current?.getApi().refetchEvents();
  }, []);

  // fetchEvents no depende de `patients` — solo de doctorId
  // Los nombres de pacientes se resuelven en eventContent con la lista en memoria
  const fetchEvents = useCallback(async (fetchInfo, successCallback, failureCallback) => {
    if (!doctorId) return successCallback([]);
    try {
      const response = await getAppointments({
        doctor_id: doctorId,
        start_date: fetchInfo.start.toISOString(),
        end_date: fetchInfo.end.toISOString(),
        limit: 500,
      });
      const events = (response.data?.items || []).map((ap) => {
        const startUTC = new Date(ap.start_time + 'Z');
        const endUTC = new Date(startUTC.getTime() + ap.duration_minutes * 60000);
        return {
          id: ap.id,
          title: ap.patient_id ? `p:${ap.patient_id}` : 'Bloqueado',
          start: toLocalISO(startUTC),
          end: toLocalISO(endUTC),
          color: getEventColor(ap),
          extendedProps: { ...ap },
        };
      });
      successCallback(events);
    } catch (err) {
      failureCallback(err);
    }
  }, [doctorId]);

  const handleDateClick = useCallback((info) => {
    if (!canCreate) return;
    setIsEdit(false);
    setSelectedEvent({ start_time: info.dateStr });
    setOpenDialog(true);
  }, [canCreate]);

  const handleEventClick = useCallback((info) => {
    setIsEdit(true);
    setSelectedEvent(info.event.extendedProps);
    setOpenDialog(true);
  }, []);

  const handleEventDrop = useCallback(async (info) => {
    const ap = info.event.extendedProps;
    try {
      await updateAppointment(ap.id, {
        start_time: info.event.start.toISOString(),
        duration_minutes: ap.duration_minutes,
      });
      setTimeout(refreshCalendar, 300);
    } catch (err) {
      info.revert();
      notify(err.userMessage || err.response?.data?.detail || 'No se puede mover el turno', 'error');
    }
  }, [refreshCalendar, notify]);

  const handleEventResize = useCallback(async (info) => {
    const ap = info.event.extendedProps;
    const newDuration = Math.round((info.event.end - info.event.start) / 60000);
    try {
      await updateAppointment(ap.id, {
        start_time: info.event.start.toISOString(),
        duration_minutes: newDuration,
      });
      setTimeout(refreshCalendar, 300);
    } catch (err) {
      info.revert();
      notify(err.userMessage || err.response?.data?.detail || 'No se puede cambiar la duración', 'error');
    }
  }, [refreshCalendar, notify]);

  const patientMap = useMemo(() => {
    const map = {};
    patients.forEach(p => { map[p.id] = p.name; });
    return map;
  }, [patients]);

  const handleClose = useCallback(() => setOpenDialog(false), []);

  // Estilo "pill" azul compartido por los tres grupos de controles (navegación,
  // Semana/Día, Detallada/Resumen) — un único sistema visual consistente en
  // vez del ToggleButtonGroup gris de MUI conviviendo con los botones nativos
  // (feos, sin estilizar) de FullCalendar.
  const pillGroupSx = {
    height: 32,
    '& .MuiToggleButton-root': {
      px: 1.5, fontSize: '0.78rem', textTransform: 'none', fontWeight: 500,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
      '&:hover': { bgcolor: `${theme.palette.primary.light}18` },
      '&.Mui-selected': {
        bgcolor: theme.palette.primary.main,
        color: 'white',
        borderColor: theme.palette.primary.main,
        '&:hover': { bgcolor: theme.palette.primary.dark },
      },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Barra única y compacta: navegación de fecha + Semana/Día + Detallada/Resumen */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 1, px: { xs: 1.5, sm: 2 }, py: 1,
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        {!showWeekOverview ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <IconButton size="small" onClick={goPrev} sx={{ borderRadius: 0 }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Tooltip title="Ir a hoy">
                <IconButton size="small" onClick={goToday} sx={{ borderRadius: 0, borderLeft: '1px solid', borderRight: '1px solid', borderColor: 'divider' }}>
                  <TodayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={goNext} sx={{ borderRadius: 0 }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
            {/* Ir a un día específico — click abre un mini-calendario de mes
                para saltar directo a cualquier fecha, en vez de ir de a una
                semana/día con las flechas. Pedido explícito del usuario. */}
            <Tooltip title="Ir a una fecha">
              <Box
                onClick={(e) => setDatePickerAnchor(e.currentTarget)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                  px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <CalendarMonthIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 100 }}>
                  {calendarTitle}
                </Typography>
              </Box>
            </Tooltip>
            <Popover
              open={Boolean(datePickerAnchor)}
              anchorEl={datePickerAnchor}
              onClose={() => setDatePickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DateCalendar onChange={goToDate} />
              </LocalizationProvider>
            </Popover>
          </Box>
        ) : (
          <Box />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 1 } }}>
          {!showWeekOverview && !isMobile && (
            <ToggleButtonGroup
              value={calendarView}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) changeView(v); }}
              sx={pillGroupSx}
            >
              <ToggleButton value="timeGridWeek">Semana</ToggleButton>
              <ToggleButton value="timeGridDay">Día</ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Solo aparece cuando hay días realmente ocultos (médico sin
              horario configurado ese día) — por defecto se ocultan para
              aprovechar el espacio, pero no todos quieren eso (alguien
              puede querer ver el finde vacío igual). Pedido explícito del
              usuario 2026-10-29. */}
          {!showWeekOverview && calendarView === 'timeGridWeek' && autoHiddenWeekdays.length > 0 && (
            <Tooltip title={showAllDays ? 'Volver a ocultar días sin horario' : 'Mostrar todos los días de la semana'}>
              <ToggleButton
                value="showAllDays"
                selected={showAllDays}
                onChange={() => setShowAllDays((v) => !v)}
                size="small"
                sx={{
                  ...pillGroupSx['& .MuiToggleButton-root'],
                  border: (t) => `1px solid ${t.palette.primary.light}`,
                  borderRadius: '16px !important',
                  px: isMobile ? 1 : 1.5,
                }}
              >
                {/* Mobile: solo ícono — con el texto completo esta barra
                    pasaba a 2 líneas en pantallas chicas. Pedido explícito
                    del usuario 2026-10-30. */}
                {isMobile ? <ViewWeekIcon fontSize="small" /> : (showAllDays ? 'Ocultar días libres' : 'Ver toda la semana')}
              </ToggleButton>
            </Tooltip>
          )}

          {/* Resumen semanal no se ofrece en mobile — pedido explícito del
              usuario 2026-10-30: esa fila de toggles no aportaba en una
              pantalla chica (showWeekOverview arranca en false, así que no
              hay forma de quedar "atascado" ahí al achicar la ventana). */}
          {!isMobile && (
            <ToggleButtonGroup
              value={showWeekOverview ? 'overview' : 'detail'}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) setShowWeekOverview(v === 'overview'); }}
              sx={pillGroupSx}
            >
              <ToggleButton value="detail">
                <Tooltip title="Agenda detallada — ver y editar turnos">
                  <span>Detallada</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="overview">
                <Tooltip title="Vista resumen — identificar huecos libres de un vistazo, sin scroll">
                  <span>Resumen semanal</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {showWeekOverview ? (
        <WeekOverviewGrid
          doctorId={doctorId}
          businessHours={businessHours}
          onSlotClick={(date) => {
            if (!canCreate) return;
            setIsEdit(false);
            setSelectedEvent({ start_time: toLocalISO(date) });
            setOpenDialog(true);
          }}
          onAppointmentClick={(ap) => {
            setIsEdit(true);
            setSelectedEvent(ap);
            setOpenDialog(true);
          }}
        />
      ) : (
      <Box sx={{
        flex: 1,
        px: { xs: 0, sm: 1 },
        pt: 0.5,
        minHeight: 0,
        '& .fc': {
          fontFamily: theme.typography.fontFamily,
          height: '100%',
        },
        '& .fc-timegrid-slot': { height: '2em' },
        // Línea más marcada arriba de cada hora en punto (8, 9, 10...),
        // SOLO en la columna del eje de horarios a la izquierda — no en la
        // grilla de turnos, para no competir visualmente con los bordes
        // entre turnos ya agregados. .fc-timegrid-slot-minor son los
        // slots de 15/30/45 (ya vienen con borde punteado de FullCalendar);
        // los de hora en punto no llevan esa clase. Pedido explícito del
        // usuario 2026-10-29.
        '& .fc-timegrid-axis-chunk .fc-timegrid-slot:not(.fc-timegrid-slot-minor)': {
          borderTop: `2px solid ${theme.palette.text.secondary} !important`,
        },
        '& .fc-timegrid-slot-label': { fontSize: '0.72rem', color: theme.palette.text.secondary },
        '& .fc-col-header-cell': {
          fontSize: '0.8rem',
          fontWeight: 600,
          color: theme.palette.text.secondary,
          py: 1,
        },
        '& .fc-event': {
          borderRadius: '4px !important',
          border: 'none !important',
          // Línea divisoria sutil entre turnos consecutivos — antes dos
          // bloques pegados (ej. 11:00-11:30 seguido de 11:30-12:00) se
          // veían como un solo bloque continuo sin forma de distinguir
          // dónde termina uno y empieza el otro. Un borde superior
          // translúcido (funciona sobre cualquier color de estado) alcanza
          // sin tocar el alto/posición real de cada evento. Pedido
          // explícito del usuario a partir del mockup de diseño 2026-10-29.
          borderTop: '1px solid rgba(255,255,255,0.35) !important',
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        },
        '& .fc-event:hover': {
          filter: 'brightness(0.92)',
        },
        '& .fc-now-indicator-line': {
          borderColor: theme.palette.error.main,
          borderWidth: '2px',
        },
        // Horario fuera de la agenda del médico (bloqueado) — antes casi no
        // se distinguía del hueco libre (rgba(0,0,0,0.02) era casi
        // imperceptible, y en dark mode ese mismo negro sobre fondo oscuro
        // no se notaba en absoluto). Ahora un gris sólido bien marcado +
        // rayas diagonales, con valores propios (no derivados del fondo)
        // para que el contraste sea consistente en ambos temas.
        '& .fc-non-business': {
          backgroundColor: `${theme.palette.grey[200]} !important`,
          backgroundImage: theme.palette.mode === 'dark'
            ? 'repeating-linear-gradient(135deg, rgba(0,0,0,0.25) 0px, rgba(0,0,0,0.25) 6px, transparent 6px, transparent 12px) !important'
            : 'repeating-linear-gradient(135deg, rgba(0,0,0,0.08) 0px, rgba(0,0,0,0.08) 6px, transparent 6px, transparent 12px) !important',
        },
      }}>
        <FullCalendar
          ref={calendarRef}
          locales={[esLocale]}
          locale="es"
          firstDay={1}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
          initialDate={initialCalendarDate}
          events={fetchEvents}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          datesSet={updateCalendarTitle}
          editable={canEdit}
          selectable={canCreate}
          allDaySlot={false}
          // Dinámico: 1 hora antes del inicio real del médico, redondeado
          // a hora en punto (ver cálculo de slotMinTime arriba) — antes
          // era un 07:30 fijo para cualquier médico, con dos problemas:
          // (1) desalineaba las etiquetas "cada 1 hora" porque 07:30 no es
          // una hora en punto, y (2) un médico que empieza a las 8 o 9
          // seguía mostrando franja gris de fuera-de-horario desde las
          // 7:30, mucho antes de lo necesario. Bug real reportado con
          // captura 2026-10-29.
          slotMinTime={slotMinTime}
          slotMaxTime="21:00:00"
          height="100%"
          slotDuration="00:15:00"
          slotLabelInterval="01:00"
          businessHours={businessHours}
          nowIndicator={true}
          /* Antes saltaba a la hora actual (menos 1) al abrir la agenda —
             pedido explícito del usuario: dejarla siempre al principio del
             día, no a la hora actual. Ahora el "principio del día" es la
             hora real de inicio del horario del médico (earliestStartTime),
             en vez de un 07:30 fijo para cualquier médico. */
          scrollTime={earliestStartTime}
          hiddenDays={hiddenWeekdays}
          headerToolbar={false}
          eventContent={(arg) => {
            const ap = arg.event.extendedProps;
            const isWeek = arg.view.type === 'timeGridWeek';
            const isCheckedIn = !!ap.checked_in_at;
            const patientName = ap.patient_id
              ? (patientMap[ap.patient_id] || 'Paciente')
              : '— Bloqueado —';

            const typeName = ap.appointment_type?.name;
            const typeColor = ap.appointment_type?.color;

            return (
              <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
                {typeColor && (
                  <Box title={typeName} sx={{ width: '16px', flexShrink: 0, bgcolor: typeColor }} />
                )}
                <Box
                  sx={{
                  flex: 1,
                  minWidth: 0,
                  px: isWeek ? '3px' : '6px',
                  py: '2px',
                  height: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  position: 'relative',
                  // Sobreturno ya no pisa el color de fondo (ver getEventColor) —
                  // se indica con rayas diagonales sutiles sobre el estado real.
                  ...(ap.is_overbooked && {
                    backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.12) 0px, rgba(255,255,255,0.12) 5px, transparent 5px, transparent 10px)',
                  }),
                }}>
                {/* Hora + badge del tipo de turno, en la MISMA línea — antes
                    el tipo se mostraba en letra Caveat apilado debajo del
                    nombre del paciente (o al costado del bloque entero),
                    compitiendo por ALTO en vez de ANCHO. En turnos cortos
                    (15-30min) en mobile esas líneas extra no entraban y se
                    superponían con el turno de al lado. Ahora hora y badge
                    truncan de forma independiente: la hora nunca se corta
                    (ancho fijo), el badge se angosta y trunca con "…" hasta
                    un mínimo legible. El nombre del paciente vive en su
                    propia línea aparte, nunca comparte ancho con el badge,
                    así un tipo de turno largo jamás le come espacio al
                    nombre. Bug real reportado con captura 2026-10-29. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: isWeek ? '0.67rem' : '0.75rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: 'white',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexShrink: 0,
                  }}>
                    {arg.timeText}
                  </Typography>
                  {/* Spacer ANTES del badge (no después) — hora a la
                      izquierda, tipo de turno pegado a la derecha, "en
                      puntas opuestas" del bloque en vez de juntos a la
                      izquierda. Pedido explícito del usuario 2026-10-29. */}
                  <Box sx={{ flex: 1, minWidth: 0 }} />
                  {typeName && (
                    <Typography title={typeName} sx={{
                      fontSize: isWeek ? '0.58rem' : '0.63rem',
                      fontWeight: 700,
                      lineHeight: 1.3,
                      letterSpacing: '0.2px',
                      textTransform: 'uppercase',
                      color: 'white',
                      bgcolor: 'rgba(255,255,255,0.25)',
                      borderRadius: '3px',
                      px: '4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flexShrink: 1,
                      minWidth: '12px',
                    }}>
                      {typeName}
                    </Typography>
                  )}
                  {ap.is_overbooked && (
                    <Box title="Sobreturno" sx={{
                      minWidth: 13, height: 13, px: '2px', borderRadius: '2px',
                      bgcolor: 'rgba(255,255,255,0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Typography sx={{ fontSize: '0.55rem', color: 'white', fontWeight: 900, lineHeight: 1 }}>+1</Typography>
                    </Box>
                  )}
                  {isCheckedIn && (
                    <Box sx={{
                      width: 13, height: 13, borderRadius: '2px',
                      bgcolor: 'rgba(255,255,255,0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Typography sx={{ fontSize: '0.55rem', color: 'white', fontWeight: 900, lineHeight: 1 }}>✓</Typography>
                    </Box>
                  )}
                </Box>
                <Typography sx={{
                  fontSize: isWeek ? '0.65rem' : '0.73rem',
                  lineHeight: 1.25,
                  color: 'rgba(255,255,255,0.93)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mt: '1px',
                  position: 'relative',
                }}>
                  {patientName}
                </Typography>
                {!isWeek && ap.notes && (
                  <Typography sx={{
                    fontSize: '0.63rem',
                    color: 'rgba(255,255,255,0.72)',
                    mt: '1px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    position: 'relative',
                  }}>
                    {ap.notes}
                  </Typography>
                )}
                </Box>
              </Box>
            );
          }}
        />
      </Box>
      )}

      <Legend showOutOfSchedule={showWeekOverview} />

      <AppointmentDialog
        open={openDialog}
        onClose={handleClose}
        isEdit={isEdit}
        selectedEvent={selectedEvent}
        doctorId={doctorId}
        doctor={doctor}
        patients={patients}
        setPatients={setPatients}
        waTemplates={waTemplates}
        onSaved={refreshCalendar}
      />

    </Box>
  );
};

export default CalendarGrid;
